import { forwardRef, useMemo } from 'react';

import { Box, ListItem, useSlotRecipe } from '@chakra-ui/react';

import escapeRegExp from 'lodash/escapeRegExp';

import { IconsLinear } from '@nocowanie/icons';

import { AutocompleteBaseItemProps } from './autocomplete-item.props';

export const AutocompleteBaseItem = forwardRef<HTMLLIElement, AutocompleteBaseItemProps>(
    (
        {
            caption,
            children,
            query,
            name,
            url,
            LeftIcon = IconsLinear.Location,
            isHighlighted = false,
            translationData,
            ...rest
        }: AutocompleteBaseItemProps,
        ref,
    ): JSX.Element => {
        const recipe = useSlotRecipe({ key: 'autocompleteItem' });
        const styles = recipe({
            variant: isHighlighted ? 'isHighlighted' : undefined,
        });

        const text = useMemo(() => {
            if (!query) {
                return name;
            }

            return name.replace(
                new RegExp(escapeRegExp(query), 'ig'),
                part => `<strong>${part}</strong>`,
            );
        }, [name, query]);

        return (
            <ListItem
                ref={ref}
                css={styles.wrapper}
                data-testid={'autocomplete-item'}
                className={'wc-autocomplete-item'}
                {...rest}
            >
                <LeftIcon {...styles.icon} size={'md'} />
                <Box>
                    <Box
                        css={styles.label}
                        dangerouslySetInnerHTML={{
                            __html: text,
                        }}
                    />
                    <Box fontSize={'xs'}>{caption}</Box>
                </Box>
                <Box ml={'auto'} fontSize={'sm'} flex={'0 0 auto'}>
                    {children}
                </Box>
            </ListItem>
        );
    },
);

AutocompleteBaseItem.displayName = 'AutocompleteBaseItem';
